import React, { useState } from 'react';
import { Zap, Edit2, ChevronDown, ChevronUp } from 'lucide-react';

export default function ImprovementCard({ issue, onEditManually, onFixWithAI }) {
  const [expanded, setExpanded] = useState(false);

  const getPriorityBadge = (priority) => {
    const p = String(priority).toLowerCase();
    if (p === 'high') {
      return (
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20">
          High Priority
        </span>
      );
    }
    if (p === 'medium') {
      return (
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-orange-500/10 text-orange-400 border border-orange-500/20">
          Medium Priority
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-zinc-500/10 text-zinc-400 border border-zinc-500/20">
        Low Priority
      </span>
    );
  };

  const scoreGain = issue.potential_score_gain || 0;
  const fixTime = issue.estimated_fix_time || 3;
  const fixStrategy = issue.fix_strategy || 'MANUAL';

  return (
    <div 
      className="p-5 rounded-xl transition-all duration-300 flex flex-col gap-3 relative overflow-hidden"
      style={{ 
        background: 'var(--card-bg)', 
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--card-shadow)'
      }}
    >
      {/* Clickable Header for expand/collapse */}
      <div 
        className="flex items-start justify-between gap-4 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="space-y-1.5 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span 
              className="text-sm font-bold tracking-tight hover:text-violet-400 transition-colors"
              style={{ color: 'var(--text-primary)' }}
            >
              {issue.title}
            </span>
            {getPriorityBadge(issue.priority)}
            {scoreGain > 0 && (
              <span className="px-2 py-0.5 rounded text-[10px] font-extrabold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20">
                +{scoreGain} ATS Score
              </span>
            )}
            <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{issue.id}</span>
          </div>
          <p className="text-xs leading-relaxed truncate-2-lines" style={{ color: 'var(--text-secondary)' }}>
            {issue.description}
          </p>
        </div>
        
        <div className="p-1" style={{ color: 'var(--text-secondary)' }}>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {/* Expandable Explanation Details */}
      {expanded && (
        <div 
          className="p-3.5 rounded-lg space-y-3 mt-1 animate-fadeIn"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--card-border)' }}
        >
          {issue.reason && (
            <div className="text-[11px] leading-relaxed">
              <span className="font-bold block mb-0.5" style={{ color: 'var(--text-primary)' }}>Why it matters:</span>
              <span style={{ color: 'var(--text-secondary)' }}>{issue.reason}</span>
            </div>
          )}
          {issue.impact && (
            <div className="text-[11px] leading-relaxed">
              <span className="font-bold block mb-0.5" style={{ color: 'var(--text-primary)' }}>Impact / Fix Details:</span>
              <span style={{ color: 'var(--text-secondary)' }}>{issue.impact}</span>
            </div>
          )}
          {issue.recommendation && (
            <div className="text-[11px] leading-relaxed">
              <span className="font-bold block mb-0.5" style={{ color: 'var(--brand)' }}>Recommendation:</span>
              <span style={{ color: 'var(--text-secondary)' }}>{issue.recommendation}</span>
            </div>
          )}
          <div className="text-[10px] flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
            <span>Fix duration: ~{fixTime} min</span>
            <span>•</span>
            <span>Category: <span className="capitalize">{issue.category || issue.section}</span></span>
          </div>
        </div>
      )}

      {/* Quick Action Footer */}
      <div 
        className="flex items-center justify-between gap-4 pt-3 mt-1"
        style={{ borderTop: '1px solid var(--divider)' }}
      >
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          {fixTime} min fix
        </span>
        
        <div className="flex items-center gap-1.5">
          {(fixStrategy === 'MANUAL' || fixStrategy === 'BOTH') && (
            <button 
              onClick={() => onEditManually(issue)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold active:scale-95 transition-all"
              style={{
                background: 'var(--surface-3)',
                border: '1px solid var(--card-border)',
                color: 'var(--text-primary)',
                cursor: 'pointer'
              }}
            >
              <Edit2 className="w-2.5 h-2.5" />
              Edit Manually
            </button>
          )}
          
          {(fixStrategy === 'AI' || fixStrategy === 'BOTH') && (
            <button 
              onClick={() => onFixWithAI(issue)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold active:scale-95 transition-all"
              style={{
                color: 'var(--brand)',
                background: 'var(--brand-light)',
                border: '1px solid var(--border-subtle)',
                cursor: 'pointer'
              }}
            >
              <Zap className="w-2.5 h-2.5" />
              Fix with AI (1 Credit)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
